/**
 * API client with automatic auth header injection
 */

let authHeadersGetter: (() => { Authorization: string } | null) | null = null;

/**
 * Set the auth headers getter function
 * Called by AuthProvider to inject the getAuthHeaders function
 */
export function setAuthHeadersGetter(getter: () => { Authorization: string } | null) {
  authHeadersGetter = getter;
}

/**
 * Make an authenticated API request
 */
export async function authFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const headers = new Headers(options.headers);

  // Add auth header if available
  const authHeaders = authHeadersGetter?.();
  if (authHeaders) {
    headers.set('Authorization', authHeaders.Authorization);
  }

  // Add content-type for JSON if body is present
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * API client methods
 */
export const apiClient = {
  // User profile
  async getProfile() {
    const res = await authFetch('/api/users/me');
    if (!res.ok) throw new Error('Failed to get profile');
    return res.json();
  },

  async updateProfile(data: { display_name?: string }) {
    const res = await authFetch('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to update profile');
    }
    return res.json();
  },

  async deleteAccount() {
    const res = await authFetch('/api/users/me', {
      method: 'DELETE',
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to delete account');
    }
  },

  // Registration
  async register(tempToken: string, displayName?: string) {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        temp_token: tempToken,
        display_name: displayName,
      }),
    });
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Registration failed');
    }
    return res.json();
  },
};

export default apiClient;
